import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  AlertTriangle,
  FileText,
  RefreshCw,
} from 'lucide-react';
import websiteApi from '../../services/websiteApi';
import './WebsiteContentManager.css';

interface WebsiteContentData {
  _id?: string;
  section: string;
  content: string;
  isActive: boolean;
  lastModifiedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

const SECTION_OPTIONS = [
  { value: 'all', label: 'All sections' },
  { value: 'hero_title', label: 'Hero title' },
  { value: 'hero_subtitle', label: 'Hero subtitle' },
  { value: 'features_title', label: 'Features title' },
  { value: 'pricing_title', label: 'Pricing title' },
  { value: 'pricing_subtitle', label: 'Pricing subtitle' },
  { value: 'testimonials', label: 'Testimonials' },
  { value: 'about', label: 'About' },
  { value: 'contact', label: 'Contact' },
  { value: 'footer', label: 'Footer' },
];

const WebsiteContentManager: React.FC = () => {
  const [contents, setContents] = useState<WebsiteContentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [editingContent, setEditingContent] = useState<WebsiteContentData | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSection, setSelectedSection] = useState<string>('all');
  const [formData, setFormData] = useState<WebsiteContentData>({
    section: '',
    content: '',
    isActive: true,
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  const fetchContents = useCallback(async () => {
    try {
      setLoading(true);
      const fetchedContents = await websiteApi.getWebsiteContent();
      setContents(fetchedContents || []);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch content');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContents();
  }, [fetchContents]);

  const handleSave = async () => {
    try {
      if (!formData.section || !formData.content) {
        setError('Section and content are required');
        return;
      }

      if (editingContent?._id) {
        await websiteApi.updateContent(editingContent._id, formData);
        setSuccessMessage('Content updated');
      } else {
        await websiteApi.createContent(formData);
        setSuccessMessage('Content created');
      }
      await fetchContents();
      setShowModal(false);
      setEditingContent(null);
      resetForm();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save content');
    }
  };

  const handleDelete = async (contentId: string) => {
    try {
      await websiteApi.deleteContent(contentId);
      setSuccessMessage('Content deleted');
      await fetchContents();
      setShowDeleteConfirm(null);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete content');
    }
  };

  const openEditModal = (content?: WebsiteContentData) => {
    if (content) {
      setEditingContent(content);
      setFormData(content);
    } else {
      setEditingContent(null);
      resetForm();
    }
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      section: '',
      content: '',
      isActive: true,
    });
  };

  const filteredContents = contents.filter((content) => {
    const matchesSearch =
      content.section?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      content.content?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSection = selectedSection === 'all' || content.section === selectedSection;
    return matchesSearch && matchesSection;
  });

  const formatDate = (date?: string) => {
    if (!date) return '—';
    try {
      return new Date(date).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return date;
    }
  };

  if (loading && contents.length === 0) {
    return (
      <div className="wcm-page wcm-loading">
        <RefreshCw size={20} className="wcm-spin" />
        Loading content…
      </div>
    );
  }

  return (
    <div className="wcm-page">
      <div className="wcm-toolbar">
        <input
          type="text"
          className="wcm-search"
          placeholder="Search section or text…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="wcm-select"
          value={selectedSection}
          onChange={(e) => setSelectedSection(e.target.value)}
        >
          {SECTION_OPTIONS.map((section) => (
            <option key={section.value} value={section.value}>{section.label}</option>
          ))}
        </select>
        <span className="wcm-toolbar-note">{filteredContents.length} of {contents.length} sections</span>
        <div className="wcm-toolbar-actions">
          <button type="button" className="wcm-btn" onClick={fetchContents}>
            <RefreshCw size={14} className={loading ? 'wcm-spin' : ''} />
          </button>
          <button type="button" className="wcm-btn wcm-btn--primary" onClick={() => openEditModal()}>
            <Plus size={14} /> New content
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="wcm-toast wcm-toast--ok">
          <span>{successMessage}</span>
          <button type="button" onClick={() => setSuccessMessage('')} aria-label="Dismiss"><X size={14} /></button>
        </div>
      )}
      {error && (
        <div className="wcm-toast wcm-toast--err">
          <span>{error}</span>
          <button type="button" onClick={() => setError('')} aria-label="Dismiss"><X size={14} /></button>
        </div>
      )}

      <div className="wcm-table-panel">
        {filteredContents.length === 0 ? (
          <div className="wcm-empty">
            <FileText size={28} strokeWidth={1.5} />
            <p>{searchTerm || selectedSection !== 'all' ? 'No matching content' : 'No content sections yet'}</p>
            <button type="button" className="wcm-btn wcm-btn--primary" onClick={() => openEditModal()}>
              <Plus size={14} /> Add content
            </button>
          </div>
        ) : (
          <div className="wcm-table-scroll">
            <table className="wcm-table">
              <thead>
                <tr>
                  <th style={{ width: '22%' }}>Section</th>
                  <th>Content</th>
                  <th style={{ width: '90px' }}>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredContents.map((content) => (
                  <tr key={content._id}>
                    <td>
                      <span className="wcm-section">{content.section}</span>
                    </td>
                    <td>
                      <span className="wcm-content">{content.content}</span>
                      <span className="wcm-meta">Updated {formatDate(content.updatedAt)}</span>
                    </td>
                    <td>
                      <span className={`wcm-badge ${content.isActive ? 'wcm-badge--active' : 'wcm-badge--inactive'}`}>
                        {content.isActive ? 'Active' : 'Hidden'}
                      </span>
                    </td>
                    <td>
                      <div className="wcm-actions">
                        <button type="button" className="wcm-icon-btn" title="Edit" onClick={() => openEditModal(content)}>
                          <Edit size={14} />
                        </button>
                        <button
                          type="button"
                          className="wcm-icon-btn wcm-icon-btn--danger"
                          title="Delete"
                          onClick={() => setShowDeleteConfirm(content._id!)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="wcm-overlay" onClick={() => setShowModal(false)}>
          <div className="wcm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wcm-modal-header">
              <h3 className="wcm-modal-title">{editingContent ? 'Edit content' : 'New content'}</h3>
              <button type="button" className="wcm-modal-close" onClick={() => setShowModal(false)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="wcm-modal-body">
              <div className="wcm-field">
                <label htmlFor="wcm-section">Section key</label>
                <input
                  id="wcm-section"
                  type="text"
                  value={formData.section}
                  onChange={(e) => setFormData((prev) => ({ ...prev, section: e.target.value }))}
                  placeholder="hero_title"
                  list="wcm-section-suggestions"
                />
                <datalist id="wcm-section-suggestions">
                  {SECTION_OPTIONS.filter((s) => s.value !== 'all').map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </datalist>
              </div>
              <div className="wcm-field">
                <label htmlFor="wcm-content">Content</label>
                <textarea
                  id="wcm-content"
                  value={formData.content}
                  onChange={(e) => setFormData((prev) => ({ ...prev, content: e.target.value }))}
                  placeholder="Enter the text shown on the website"
                />
              </div>
              <label className="wcm-toggle-row">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
                />
                Visible on website
              </label>
            </div>
            <div className="wcm-modal-footer">
              <button type="button" className="wcm-btn" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="button" className="wcm-btn wcm-btn--primary" onClick={handleSave}>
                <Save size={14} /> Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="wcm-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="wcm-modal wcm-modal--sm" onClick={(e) => e.stopPropagation()}>
            <div className="wcm-modal-body">
              <div className="wcm-confirm-icon"><AlertTriangle size={22} /></div>
              <p className="wcm-confirm-text">Delete this content section? It will be removed from the live website.</p>
            </div>
            <div className="wcm-modal-footer">
              <button type="button" className="wcm-btn" onClick={() => setShowDeleteConfirm(null)}>Cancel</button>
              <button type="button" className="wcm-btn wcm-btn--danger" onClick={() => handleDelete(showDeleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteContentManager;
